// STEP 1'S WORDS ARE THE APP'S (WEB-PARITY-1C). Tyler, 2026-09-29: "It should model the app perfectly".
//
// Every label, caption, notice and alert on `/details` that the server does not send is the app's own string, copied
// unchanged from the line it cites (under `AppealAce/`), as `heldCopy.ts`'s APP_WORDING is for WEB-PARITY-1B. A
// `{name}` is the app's own interpolation written the web's way (`\(year)` → `{year}`); nothing else is changed. They are
// not new sentences, so they carry no new approval: the approval is Tyler's standing instruction (`WEB_PARITY`), and
// `test/detailsParity.test.tsx` reads each cited line and fails when the app's words and these differ.
//
// A TABLE OF ITS OWN, NOT APP_WORDING'S, only so that this lane and the pre-payment lane (2B), running at the same time,
// do not both append to one object. The shape, the approval and the pin are APP_WORDING's.
//
// NOT HERE, ON PURPOSE — the app's words that would name a route this website does not have yet (R112, the route half):
//   - the Identification card's footer (SubjectPropertyView.swift:1560), whose second sentence sends the owner to
//     "Delete this draft", which the website has no control for (F-WP1C-3: held until the website can delete a draft);
//   - the refusal card's "Check the address" (:1037): the address is locked once a credit is applied, on both clients.
// The ratio card's status line (:2071, :2060) was held here until WEB-PARITY-2A built the override it asks for (DT-15).
//
// HERE BUT HELD — the app's reminder sentences (F-EM-9): the Jurisdiction footer (:2441) says "We remind you…", and the
// website sends no reminders yet. It is in this table so its words stay pinned to the app's, and `Details.tsx` renders it
// only when `WEBSITE_REMINDERS_LIVE` says reminders exist — which today it does not.

import { WEB_PARITY, type AppWording } from "../../components/heldCopy";

function app(appSource: string, text: string): AppWording {
  return { text, status: "appWording", approval: WEB_PARITY, appSource };
}

const SPV = "Features/Subject/SubjectPropertyView.swift";
/** PURCHASE-FIRST (R182) moved the recent-purchase card's sentences here from `SubjectPropertyView`, verbatim. */
const RPC = "Features/Subject/RecentPurchaseCopy.swift";
const MODELS = "Models/Models.swift";
const RV = "Features/Review/ReviewView.swift";
const RS = "Services/ReminderService.swift";
const CE = "Features/Comps/CompEditView.swift";

export const DETAILS_WORDING = {
  // ── the hero (DT-1) ──
  "details.hero.title": app(`${SPV}:1336`, "Confirm your details"),
  "details.hero.body": app(
    `${SPV}:1339`,
    "Here's what we have for your property — fix anything that's off and fill in what's missing. The more accurate, the stronger your appeal.",
  ),

  // ── the satellite view's caption (DT-2) ──
  "details.satellite.caption": app("Shared/SatelliteMapCard.swift:40", "Satellite"),

  // ── Identification (DT-7, DT-8) ──
  "details.identification.title": app(`${SPV}:1558`, "Identification"),
  "details.address.label": app(`${SPV}:1610`, "Property address"),
  "details.unit.label": app(`${SPV}:1637`, "Unit"),
  "details.parcel.label": app(`${SPV}:1716`, "Parcel ID / APN"),
  "details.parcel.placeholder.required": app(`${MODELS}:996`, "Parcel / account number from your notice"),
  "details.parcel.placeholder.optional": app(`${MODELS}:997`, "Add your parcel / account number (optional)"),

  // ── Value (DT-9, DT-10) ──
  "details.value.title": app(`${SPV}:1785`, "Value"),
  "details.value.assessed": app(`${SPV}:1775`, "Assessed value"),
  "details.value.year": app(
    `${SPV}:1803`,
    "This is the {year} assessed value on file — check it matches your current notice. Property valuations can lag the county's latest records, so verify before relying on it.",
  ),
  "details.value.helper": app(`${SPV}:1815`, "The assessed value on your county notice — the number the county taxes you on."),

  // ── Assessment ratio (DT-13, DT-14) ──
  "details.ratio.title": app(`${SPV}:2022`, "Assessment ratio"),

  // ── Characteristics (DT-16 to DT-22) ──
  "details.characteristics.title": app(`${SPV}:2283`, "Characteristics"),
  "details.characteristics.footer": app(
    `${SPV}:2284`,
    "These drive the comp comparison — the more accurate, the stronger the appeal. Double check for accuracy.",
  ),
  "details.type.label": app(`${SPV}:2300`, "Property type"),
  "details.select": app(`${SPV}:2302`, "Select"),
  "details.type.singleFamily": app(`${SPV}:3105`, "Single-family"),
  "details.type.twin": app(`${SPV}:3105`, "Twin / Semi-detached"),
  "details.type.townhouse": app(`${SPV}:3105`, "Townhouse"),
  "details.type.condo": app(`${SPV}:3105`, "Condo"),
  "details.type.multiFamily": app(`${SPV}:3105`, "Multi-family"),
  "details.type.manufactured": app(`${SPV}:3105`, "Manufactured"),
  "details.type.other": app(`${SPV}:3105`, "Other"),
  "details.type.unsupported.manufactured.title": app(`${SPV}:2322`, "Manufactured homes aren't fully supported yet"),
  "details.type.unsupported.rental.title": app(`${SPV}:2323`, "Rental buildings aren't fully supported yet"),
  "details.type.unsupported.manufactured.body": app(
    `${SPV}:2328`,
    "Manufactured homes are often taxed as personal property rather than real estate, especially on leased or park land — a separate roll with its own process — and counties usually value them from depreciation schedules rather than nearby sales. Check your notice to confirm which applies to you. If the county's record is wrong about this being manufactured, correct the type above.",
  ),
  "details.type.unsupported.rental.body": app(
    `${SPV}:2329`,
    "We value a home by comparing it to recent nearby sales. A rental building is valued on the income it produces instead, and we can't yet source the capitalization rate that takes. You can still file on condition, a record error, or the uniformity ground — and if the county's record is wrong about this being multi-unit, correct the type above.",
  ),
  "details.sqft.label": app(`${SPV}:2351`, "Square footage"),
  "details.sqft.unit": app(`${SPV}:2351`, "sq ft"),
  "details.sqft.favorable": app(
    `${SPV}:2362`,
    "County record: {record} sq ft. We’ll cite this as a factual record error, and your comps are adjusted to your corrected {corrected} sq ft.",
  ),
  "details.sqft.unfavorable": app(
    `${SPV}:2372`,
    "County record: {record} sq ft — you've entered {corrected}. Your comps will be matched and adjusted to {corrected} sq ft, which is the honest basis. We won't raise this in your letter: telling the board your home is larger than their record argues for a higher value, not a lower one.",
  ),
  "details.beds.label": app(`${SPV}:2381`, "Bedrooms"),
  "details.baths.label": app(`${SPV}:2390`, "Bathrooms"),
  "details.lot.label": app(`${SPV}:2407`, "Lot size"),
  "details.year.label": app(`${SPV}:2410`, "Year built"),
  "details.year.placeholder": app(`${SPV}:2410`, "YYYY"),

  // ── the requirement sentence and Continue (DT-24) ──
  "details.continue": app(`${SPV}:553`, "Continue"),
  "details.required.assessedValue": app(`${MODELS}:1047`, "assessed value"),
  "details.required.parcel": app(`${MODELS}:1049`, "parcel or account number from your notice"),
  "details.required.one": app(`${MODELS}:1053`, "{field} is required."),
  "details.required.two": app(`${MODELS}:1056`, "{fields} and {last} are required."),

  // ── the alerts on Continue (DT-25) ──
  "details.alert.p1.title": app(`${SPV}:394`, "Enter your current-year assessed value"),
  "details.alert.p1.enter": app(`${SPV}:595`, "Enter my current value"),
  "details.alert.cancel": app(`${SPV}:599`, "Cancel"),
  "details.alert.new2.title": app(`${SPV}:603`, "Double-check your assessed value?"),
  "details.alert.new2.edit": app(`${SPV}:604`, "Edit value"),
  "details.alert.new2.correct": app(`${SPV}:608`, "It's correct"),
  "details.alert.new2.message": app(
    `${SPV}:613`,
    "{value} looks far too low for a home — public records sometimes return a partial or land-only figure. Check it against your assessment notice; the whole comp comparison keys off this number.",
  ),
  "details.alert.new7.title": app(`${SPV}:631`, "Your own purchase contradicts a price appeal"),
  "details.alert.new7.show": app(`${SPV}:632`, "Show me"),
  "details.alert.new7.message": app(
    `${SPV}:636`,
    "You paid more for this home than the county's assessment implies it is worth, so there is no over-assessment to argue on price. The card above explains it and shows what you can still do.",
  ),
  "details.alert.new3.title": app(`${SPV}:638`, "We need your home's square footage"),
  "details.alert.new3.add": app(`${SPV}:639`, "Add it"),
  "details.alert.new3.message": app(
    `${SPV}:646`,
    "Your square footage is what lets us compare your home to recent sales and size-adjust each one to yours. Without it we can't size-adjust anything, and the comparison we'd put in front of the board would be far weaker than what you're paying for.\n\nYou'll find it on your assessment notice or your county property record card — it's also usually on Zillow or Redfin under \"interior\". An approximate figure is fine; it just needs to be about right.",
  ),

  // ── NEW-7's grounds route: the app card's third route, the one a purchase cannot rule out ──
  "details.grounds.heading": app(`${RPC}:37`, "What you can still do"),
  "details.grounds.title": app(`${RPC}:46`, "My home has a condition or record problem"),
  "details.grounds.detail": app(
    `${RPC}:49`,
    "A purchase price can't see a failing roof or a wrong square footage. Continue, and the next step is where you tell us — your appeal will rest on that, not on price.",
  ),
  "details.grounds.chosen": app(`${RPC}:51`, "Continuing on condition/record grounds — not on price."),

  // ═══ WEB-PARITY-2A ═══════════════════════════════════════════════════════════════════════════════════════════════

  // ── the record the owner is asked about: a near miss, or a building's parcels (DT-29, DT-30) ──
  "details.match.title": app(`${SPV}:894`, "Is this your property?"),
  "details.match.nearMiss": app(
    `${SPV}:899`,
    "Public records matched a different address: {address}. Confirm it's the same property before we use its data — otherwise keep entering your details manually below.",
  ),
  "details.match.yes": app(`${SPV}:975`, "Yes, that's it"),
  "details.match.yes.unit": app(`${SPV}:975`, "Yes — that matches my notice"),
  "details.match.no": app(`${SPV}:980`, "No, use what I entered"),
  "details.match.none": app(`${SPV}:980`, "None of these — I'll enter my details"),
  "details.unit.named.none": app(`${SPV}:2658`, "the unit you entered"),
  "details.unit.named": app(`${SPV}:2658`, "unit {unit}"),
  "details.unit.tail": app(
    `${SPV}:2659`,
    "Each unit in this building is assessed as its own parcel — using the wrong one would file your appeal against someone else's home.",
  ),
  "details.unit.matched": app(`${SPV}:2661`, "Public records matched {address}, which we can't confirm is {named}. {tail}"),
  "details.unit.unlabelled": app(
    `${SPV}:2665`,
    "Public records list {count} separate parcels at this address, and none of them carries a unit number — so we can't tell which one is {named}. {tail}",
  ),
  "details.unit.labelled": app(
    `${SPV}:2667`,
    "Public records list {count} separate parcels at this address. {labelled} carry a unit number, but none of them is {named} — so we can't tell which one is yours. {tail}",
  ),
  "details.unit.heading": app(`${SPV}:911`, "Which one is yours?"),
  "details.unit.caption": app(`${SPV}:2672`, "Check against your assessment notice — each unit here is a separate parcel."),
  "details.unit.caption.truncated": app(
    `${SPV}:2674`,
    "{base} Showing the first {shown} parcels at this address — there are more; filter to find yours.",
  ),
  "details.unit.caption.all": app(`${SPV}:2676`, "{base} All {shown} parcels at this address are listed."),
  "details.unit.filter": app(`${SPV}:927`, "Parcel number or assessed value"),
  "details.unit.filter.clear": app(`${SPV}:939`, "Clear filter"),
  "details.unit.filter.none": app(
    `${SPV}:952`,
    "No parcel here matches “{filter}”. Clear the filter to see all {count}, or enter your details below.",
  ),
  "details.unit.row.unit": app(`${SPV}:2692`, "Unit {unit}"),
  "details.unit.row.assessed": app(`${SPV}:2695`, "Assessed {value}"),
  "details.unit.row.noAssessed": app(`${SPV}:2695`, "Assessed value not on file"),
  "details.unit.row.sqft": app(`${SPV}:2737`, "{sqft} sq ft"),
  "details.unit.row.beds": app(`${SPV}:2738`, "{beds} bed{s}"),
  "details.unit.row.baths": app(`${SPV}:2741`, "{baths} bath{s}"),
  "details.unit.row.built": app(`${SPV}:2743`, "built {year}"),
  "details.unit.row.bought": app(`${SPV}:2752`, "Bought {when} for {price}"),
  "details.unit.row.lastSold": app(`${SPV}:2753`, "Last sold {when}"),
  "details.unit.row.lastSoldFor": app(`${SPV}:2754`, "Last sold for {price}"),
  "details.unit.row.parcel": app(`${SPV}:2717`, "Parcel {parcel}"),
  "details.unit.confirm.title": app(`${SPV}:651`, "File against this parcel?"),
  "details.unit.confirm.use": app(`${SPV}:655`, "Use this parcel"),
  "details.unit.confirm.this": app(`${SPV}:2779`, "This parcel"),
  "details.unit.confirm.message": app(
    `${SPV}:2780`,
    "{facts}\n\nYour appeal will be filed against this parcel, so it needs to match your assessment notice.",
  ),
  "details.unit.wrong": app(`${SPV}:1588`, "Wrong parcel? Choose a different unit"),
  "details.unit.gaps": app(
    `${SPV}:2293`,
    "Public records don't list the {list} for your unit — please fill {pronoun} in. {Pronoun} decide which sales you're compared against.",
  ),
  "details.unit.gaps.it": app(`${SPV}:2293`, "it"),
  "details.unit.gaps.them": app(`${SPV}:2293`, "them"),
  "details.unit.gaps.It": app(`${SPV}:2293`, "It"),
  "details.unit.gaps.They": app(`${SPV}:2293`, "They"),
  "details.unit.gap.sqft": app(`${SPV}:2852`, "square footage"),
  "details.unit.gap.beds": app(`${SPV}:2853`, "bedrooms"),
  "details.unit.gap.baths": app(`${SPV}:2854`, "bathrooms"),
  "details.unit.gap.year": app(`${SPV}:2855`, "year built"),
  "details.unit.list.two": app(`${SPV}:2845`, "{a} and {b}"),

  // ── the manual path (DT-28) ──
  "details.refusal.title": app(`${SPV}:1012`, "We couldn’t find that address in the property records we use"),
  "details.refusal.enter": app(`${SPV}:1022`, "Enter details from your assessment notice"),
  "details.refusal.thatAddress": app(`${SPV}:1081`, "that address"),
  "details.refusal.noResolvedAddress": app(
    `${SPV}:1084`,
    "We couldn’t match {address} to a specific property, so we haven’t filled anything in — we won’t use a nearby one instead. Enter your details from your assessment notice below.",
  ),
  "details.refusal.houseNumber": app(
    `${SPV}:1086`,
    "The records came back for a different house number than {address}, so we haven’t used them. Check the number, or enter your details from your assessment notice below.",
  ),
  "details.refusal.street": app(
    `${SPV}:1088`,
    "The records came back for a different street than {address}, so we haven’t used them. Check the street, or enter your details from your assessment notice below.",
  ),
  "details.refusal.droppedSubUnit": app(
    `${SPV}:1093`,
    "The records came back for the building or the main address, not the specific lot or unit in {address} — that is a different parcel, so we haven’t used them. Enter your details from your assessment notice below.",
  ),
  "details.refusal.directional": app(
    `${SPV}:1099`,
    "The records came back for a different compass direction — North, South, East or West — than {address}. Those are different properties on the same street name, so we haven’t used them. Check the direction, or enter your details from your assessment notice below.",
  ),
  "details.unreachable.title": app(`${SPV}:1162`, "We couldn’t reach the records service"),
  "details.unreachable.body": app(
    `${SPV}:1165`,
    "That’s on us, not your address — and it usually clears in a moment. Try again, or enter the details from your assessment notice instead.",
  ),
  "details.unreachable.retry": app(`${SPV}:1172`, "Try again"),
  "details.unreachable.manual": app(`${SPV}:1175`, "Enter details from my notice"),
  "details.building.title": app(`${SPV}:1134`, "Public records here cover the whole building"),
  "details.building.body": app(
    `${SPV}:1137`,
    "The county returned one parcel for this address, and its size and value describe the entire building rather than your unit — so we haven’t filled anything in from it. Enter your unit’s figures from your assessment notice and the appeal is built from those.",
  ),
  "details.owner.title": app(`${SPV}:1210`, "Enter these from your assessment notice"),
  "details.owner.unreachable": app(
    `${SPV}:1217`,
    "We couldn’t reach the records service, so these figures come from you. Copy them from the notice your county sent — your letter will state that they’re yours.",
  ),
  "details.owner.notFound": app(
    `${SPV}:1218`,
    "We couldn’t find your property in the records we use, so these figures come from you. Copy them from the notice your county sent — your letter will state that they’re yours.",
  ),
  "details.owner.noParcel": app(
    `${SPV}:1223`,
    "No parcel number on your notice? Contact your county assessor — your property may be assessed differently than expected.",
  ),
  "details.partial.needsValue": app(`${SPV}:1378`, "Add your assessed value to continue"),
  "details.partial.filled": app(`${SPV}:1379`, "We filled in what public records had"),
  "details.partial.body": app(
    `${SPV}:1385`,
    "We pulled everything available from public records for your property — a few details weren't on file. Please fill in anything still blank below (like square footage, beds, baths, or year built). The more complete, the stronger your appeal.",
  ),
  // `assessedValueMissingExplanation` (:1076-1087), a sentence the app writes across seven literals, kept as it splits them.
  "details.partial.action.1": app(`${SPV}:1066`, "Enter the assessed value from your assessment notice in the Values section "),
  "details.partial.action.2": app(`${SPV}:1067`, "below; it's the one field required to continue. Fill in anything else still blank "),
  "details.partial.action.3": app(`${SPV}:1068`, "(size, beds, baths, year built) while you're here — the more complete, the stronger "),
  "details.partial.action.4": app(`${SPV}:1069`, "your appeal."),
  "details.partial.condo.1": app(`${SPV}:1071`, "Public records didn't carry an assessed value for your unit — common in a "),
  "details.partial.condo.2": app(`${SPV}:1072`, "multi-unit building, where the lookup lands on the building rather than the "),
  "details.partial.condo.3": app(`${SPV}:1073`, "individual home. "),
  "details.partial.house": app(`${SPV}:1075`, "Public records had your property but no assessed value on file for it. "),

  // ── the recent purchase (DT-31, DT-32) ──
  "details.purchase.title": app(`${RPC}:10`, "Your own purchase contradicts a price appeal"),
  "details.purchase.paid": app(`${RPC}:17`, "You bought this home for {purchase}{when}. "),
  "details.purchase.when": app(`${RPC}:16`, " in {month}"),
  "details.purchase.implies": app(`${RPC}:18`, "Your assessment implies a market value of {implied} — so you paid MORE "),
  "details.purchase.against.1": app(
    `${RPC}:19`,
    "than the county says it is worth. An appeal arguing the price is too high would be arguing ",
  ),
  "details.purchase.against.2": app(`${RPC}:20`, "against your own sale, and it is the one piece of evidence a board can verify instantly."),
  "details.purchase.risk.cannotRaiseAtBoard": app(
    `${RPC}:27`,
    "Your board cannot raise your assessment on your own appeal, so filing is not a tax risk — but on price alone there is nothing here to win.",
  ),
  "details.purchase.risk.canRaise": app(
    `${RPC}:29`,
    "In your state a board can RAISE your assessment on your own appeal. Filing this on price could cost you money rather than save it.",
  ),
  "details.purchase.risk.deNovoRisk": app(
    `${RPC}:31`,
    "In your state an escalation is reviewed fresh and can end higher than it started. Filing this on price could cost you money rather than save it.",
  ),
  "details.purchase.risk.unknown": app(
    `${RPC}:33`,
    "In most states a board can RAISE an assessment on the owner's own appeal, and we treat that as possible here. Filing this on price could cost you money rather than save it.",
  ),
  "details.purchase.wrongSale.title": app(`${RPC}:39`, "That sale isn't right"),
  "details.purchase.wrongSale.detail": app(`${RPC}:41`, "Wrong price or wrong date — correct it below and we'll re-check."),
  "details.purchase.notMarket.title": app(`${RPC}:43`, "That wasn't an open-market sale"),
  "details.purchase.notMarket.detail": app(
    `${RPC}:44`,
    "A foreclosure, a transfer between family, or a sale to an investor isn't evidence of market value. Tell us and the price argument is back on the table.",
  ),
  // The sale's own fields, in the app's words for a sale's price and date (the comp editor's), since the app's step 1
  // has no field of its own for route 1 to scroll to (the D9 defect this does not copy).
  "details.sale.price": app(`${CE}:143`, "Sale price"),
  "details.sale.date": app(`${CE}:146`, "Sale date"),

  // ── the low figure, said where it is (DT-33), and the prompt's title the server now decides (F-WP1C-4) ──
  "details.low.delaware": app(
    `${SPV}:370`,
    "This looks low for a Delaware home. Delaware reassessed every property to current market value in 2023–2025, so an automated record may still show an outdated pre-reassessment figure — enter your current assessed value from your latest notice for an accurate comparison.",
  ),
  "details.low.vintage": app(
    `${SPV}:373`,
    "This looks like an old base-year value. Your county recently reassessed to full market value, so the figure on file may be from the previous assessment — enter your current-year assessed value from your reassessment notice for an accurate comparison.",
  ),
  "details.low.taxBill": app(
    `${SPV}:378`,
    "{shown} looks like your annual tax bill, not your assessed value. Your county's assessed value for this property is the number your appeal is based on — check your assessment notice and enter that figure.",
  ),
  "details.low.taxBill.none": app(`${SPV}:377`, "This figure"),
  "details.low.fractional": app(
    `${SPV}:384`,
    "This is too low to be your assessed value. At your county's assessment ratio it implies a market value far below what this home is worth — check your assessment notice and enter the assessed value shown there.",
  ),
  "details.low.general": app(
    `${SPV}:386`,
    "This looks unusually low for a home — double-check it against your assessment notice. Public records sometimes return a partial or land-only value.",
  ),
  "details.alert.p1.taxBill.title": app(`${SPV}:394`, "That looks like your tax bill"),
  "details.alert.p1.message.taxBill": app(
    `${SPV}:399`,
    "{shown} looks like your annual tax bill, not your assessed value — they're different numbers, and your appeal is based on the assessed value. Enter the assessed value shown on your county assessment notice.",
  ),
  "details.alert.p1.message.vintage": app(
    `${SPV}:402`,
    "Your county recently reassessed, and the value on file ({shown}) looks like the old base-year figure. Enter your current-year assessed value from your reassessment notice — that's the number you'd actually appeal.",
  ),
  "details.alert.p1.message.general": app(
    `${SPV}:404`,
    "The value on file ({shown}) is too low to be your assessed value — at your county's assessment ratio it implies a market value far below what this home is worth. Enter the assessed value from your assessment notice.",
  ),
  "details.alert.p1.none": app(`${SPV}:397`, "the value on file"),
  "details.taxYear.label": app(`${SPV}:1860`, "Tax year"),
  "details.taxYear.helper": app(`${SPV}:1863`, "The assessment year you're appealing — check your notice."),

  // ── the ratio from the owner's notice (DT-15) ──
  "details.ratio.field.pa": app(`${SPV}:2020`, "Common Level Ratio from your notice"),
  "details.ratio.field.other": app(`${SPV}:2025`, "Ratio from your notice"),
  "details.ratio.unit": app(`${SPV}:2026`, "%"),
  "details.ratio.term.pa": app(`${SPV}:2021`, "county's published STEB Common Level Ratio"),
  "details.ratio.applied": app(
    `${SPV}:2061`,
    "We've applied your {term} of about {pct} of market. Confirm it against your notice, or enter your exact ratio to override.",
  ),
  "details.ratio.expired": app(
    `${SPV}:2050`,
    "The most recent {term} we have is about {pct} of market, and it expired{on}. Check the ratio on your assessment notice and enter it above — we'll keep using ours until you do.",
  ),
  "details.ratio.expired.on": app(`${SPV}:1914`, " on {date}"),
  "details.ratio.entered": app(
    `${SPV}:2033`,
    "We'll treat your assessed value as {pct} of market value when comparing it to recent sales.",
  ),
  "details.ratio.check": app(
    `${SPV}:1957`,
    "That ratio implies a market value of {implied}, far from the {onFile} we have on file for this home. Double-check the percentage against your notice — a common slip is typing \"1\" for \"10\".",
  ),
  "details.ratio.generic": app(
    `${SPV}:2073`,
    "Some places assess your home at a percentage of its market value, not the full value. If your notice shows a percentage like this (sometimes called an “equalization ratio,” “RAR,” or “director's ratio”), enter it. Otherwise leave this blank and we'll treat your assessed value as full market value.",
  ),

  // ── Jurisdiction (DT-23) ──
  "details.jurisdiction.title": app(`${SPV}:2430`, "Jurisdiction"),
  "details.jurisdiction.footer": app(
    `${SPV}:2431`,
    "We remind you about 45 days before your appeal window closes. We pre-fill your state's earliest deadline — enter the exact date from your notice to make the reminder precise.",
  ),
  "details.jurisdiction.county": app(`${SPV}:2433`, "County"),
  "details.jurisdiction.state": app(`${SPV}:2442`, "State"),
  "details.jurisdiction.hasDeadline": app(`${SPV}:2452`, "Filing deadline"),
  "details.jurisdiction.deadline": app(`${SPV}:2456`, "Appeal must be filed by"),
  // THE APP'S NON-REMINDER WORDS FOR THE DEADLINE (F-EM-9): the Review screen's row, "Deadline · around August 1 — our
  // estimate, confirm the exact date on your notice" (ReviewView.swift:661-662, the phrase ReminderService.swift:1196).
  "details.deadline.title": app(`${RV}:686`, "Deadline"),
  "details.deadline.estimate": app(`${RV}:687`, " — our estimate, confirm the exact date on your notice"),
  "details.deadline.around": app(`${RS}:1196`, "around {date}"),
} as const satisfies Record<string, AppWording>;

export type DetailsWordingId = keyof typeof DETAILS_WORDING;

export function words(id: DetailsWordingId): string {
  return DETAILS_WORDING[id].text;
}

/** A sentence with its `{name}` slots filled. A slot left unfilled is a thrown error, never printed braces. */
export function wordsWith(id: DetailsWordingId, values: Readonly<Record<string, string | number>>): string {
  return words(id).replace(/\{(\w+)\}/g, (_, name: string) => {
    if (!Object.hasOwn(values, name)) throw new Error(`wordsWith(${id}): no value for {${name}}`);
    return String(values[name]);
  });
}
